/**
 * @file RequestCredentialsForm.js
 * @description Represents the forgot password form.
 */
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { sendForgotPasswordEmail } from '../../../services/authService';
import { useTheme } from '../../../store/ThemeContext';
import CompanyCodeForm from '../CompanyCodeForm';
import styles from '../AuthPage.module.scss';
import Toast from '../../ui/toasts/Toast';
import PlaceholderLogo from '../../ui/PlaceholderLogo';
import { getText } from '../../../i18n';

function RequestCredentialsForm({ handleAuthError, authData }) {
    const themeStyles = useTheme();
    const { companyCode: initialCompanyCode, companyConfig } = authData;
    const [countdown, setCountdown] = useState(0);
    const [isResetEmailSent, setIsResetEmailSent] = useState(false);
    const [isSendingResetEmail, setIsSendingResetEmail] = useState(false);
    const [showCompanyCodeForm, setShowCompanyCodeForm] =
        useState(!initialCompanyCode);

    const REQUEST_RESET_COUNTDOWN = 60; // seconds

    const [formState, setFormState] = useState({
        email: '',
        companyCode: initialCompanyCode || '',
    });
    const [validationState, setValidationState] = useState({
        emailClassName: '',
    });

    const [searchParams] = useSearchParams();
    const resetLinkExpired = searchParams.get('resetLinkExpired') === 'true';
    const usernameFromQuery = searchParams.get('username');

    useEffect(() => {
        if (usernameFromQuery && !formState.email) {
            setFormState(prev => ({
                ...prev,
                email: usernameFromQuery,
            }));
        }
    }, [usernameFromQuery, formState.email]);

    useEffect(() => {
        let timer = null;
        if (countdown > 0) {
            timer = setTimeout(() => setCountdown(countdown - 1), 1000);
        }
        return () => clearTimeout(timer);
    }, [countdown]);

    const handleChange = event => {
        const { name, value } = event.target;
        setFormState(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async event => {
        event.preventDefault();
        if (countdown > 0) return;

        if (validateForm()) {
            try {
                setIsSendingResetEmail(true);
                setCountdown(REQUEST_RESET_COUNTDOWN); // start countdown
                await sendForgotPasswordEmail(
                    formState.companyCode,
                    formState.email,
                );
                setIsResetEmailSent(true);
            } catch (error) {
                console.error('Forgot password request failed:', error);
                if (error?.response?.data?.detail) {
                    Toast.error(error.response.data.detail);
                } else {
                    Toast.error(getText('msg_c5bccd425389'));
                }
            }
            setIsSendingResetEmail(false);
            setTimeout(() => {
                setCountdown(0);
            }, REQUEST_RESET_COUNTDOWN * 1000);
        }
    };

    const validateForm = () => {
        let isFormValid = true;
        const updatedValidationState = {};

        if (!formState.email.trim()) {
            updatedValidationState.emailClassName = 'is-invalid';
            isFormValid = false;
        } else {
            updatedValidationState.emailClassName = '';
        }

        setValidationState(updatedValidationState);

        return isFormValid;
    };

    const handleCompanyCodeSubmit = submittedCompanyCode => {
        const currentPath = window.location.pathname;
        const newUrl = `${currentPath}?companyCode=${submittedCompanyCode}`;
        window.location.href = newUrl;
    };

    const handleBackToLogin = () => {
        window.location.href = `/login${
            initialCompanyCode ? `?companyCode=${initialCompanyCode}` : ''
        }`;
    };

    const getHelpLink = () => (
        <a
            href={`/get-support${
                initialCompanyCode ? `?companyCode=${initialCompanyCode}` : ''
            }`}
            className={`btn-link btn ps-2 ${isResetEmailSent && 'text-start'}`}
        >
            {getText('msg_97e2e9fd5798')}
        </a>
    );

    return (
        <>
            <small
                className={`${styles.returnToLoginButton} position-absolute`}
                onClick={handleBackToLogin}
            >
                <i className="bi bi-chevron-left ms-3" />{' '}
                {getText('msg_b5cd32158b70')}
            </small>
            {}

            <div
                className={`form-group text-center my-3 ${styles.logoContainer}`}
            >
                {themeStyles?.primaryLogo ||
                (formState.companyCode === 'swmockup' && '/SWMockup.png') ? (
                    <img
                        src={
                            themeStyles?.primaryLogo ||
                            (formState.companyCode === 'swmockup' &&
                                '/SWMockup.png')
                        }
                        alt={getText('msg_101d2406160e')}
                    />
                ) : (
                    <PlaceholderLogo />
                )}
            </div>

            {resetLinkExpired && (
                <div className="alert alert-danger">
                    {getText('msg_5c55b0964f02')}
                </div>
            )}

            {showCompanyCodeForm ? (
                <CompanyCodeForm
                    onSubmit={handleCompanyCodeSubmit}
                    initialCompanyCode={formState.companyCode}
                />
            ) : (
                <form
                    className="needs-validation p-3"
                    onSubmit={handleSubmit}
                    noValidate
                >
                    <div className="form-container mt-3">
                        {!isResetEmailSent && (
                            <>
                                <h2 className="iNGAGED-Typography-Heading3 mb-3">
                                    {getText('msg_3fb75e3bfe4d')}
                                </h2>
                                <small className="text-muted">
                                    {getText('msg_21c09c55409e')}
                                </small>

                                <div className="form-group text-left py-5 px-3">
                                    <label htmlFor="email">
                                        {getText('msg_84c29015de33')}
                                        <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className={`form-control ${validationState.emailClassName}`}
                                        placeholder={getText(
                                            'msg_00c5abfce907',
                                        )}
                                        name="email"
                                        id="email"
                                        autoComplete="email"
                                        onChange={handleChange}
                                        value={formState.email}
                                        aria-describedby="envelope-icon"
                                        readOnly={isResetEmailSent}
                                    />
                                    <div className="invalid-feedback">
                                        {getText('msg_c79fce0e911c')}
                                    </div>
                                </div>

                                {/* <div className="form-group text-left py-5 px-3">
                  <label htmlFor="email">
                    Login Email&nbsp;<span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${validationState.emailClassName}`}
                    placeholder="Username"
                    name="email"
                    id="email"
                    autoComplete="email"
                    onChange={handleChange}
                    value={formState.email}
                    aria-describedby="envelope-icon"
                    readOnly={isResetEmailSent}
                  />
                  <div className="invalid-feedback">Email is required.</div>
                </div> */}
                            </>
                        )}

                        <div className="form-group d-grid gap-2 col-10 mx-auto text-center">
                            {isResetEmailSent && (
                                <>
                                    <div
                                        className={`mb-1 iNGAGED-Success-Color ${styles.passwordResetCheckmark}`}
                                    >
                                        <i className="bi bi-check-circle"></i>
                                    </div>
                                    <h2 className="iNGAGED-Typography-Heading3 mb-3">
                                        {getText('msg_84c6453784fe')}
                                    </h2>
                                    <small className="pb-2 text-muted">
                                        {getText('msg_1f89ff88f957')}
                                    </small>
                                </>
                            )}

                            {!isResetEmailSent ? (
                                <>
                                    <button
                                        type="submit"
                                        className={`btn mb-2 btn-primary mx-auto p-4 pt-2 pb-2`}
                                    >
                                        {getText('msg_a55c0b7a3f4f')}
                                    </button>
                                </>
                            ) : (
                                <>
                                    <p className="d-block pt-2 pb-0 mb-0 mt-3">
                                        {getText('msg_f5582092f4f9')}
                                    </p>
                                    <div
                                        className={`d-flex pb-2 ${styles.helpButtons} d-flex justify-content-center`}
                                    >
                                        <button
                                            type="submit"
                                            className={`btn-link btn ${
                                                countdown > 0
                                                    ? 'text-muted'
                                                    : ''
                                            } pl-2 text-end pe-2`}
                                            disabled={countdown > 0}
                                        >
                                            {isSendingResetEmail
                                                ? 'Sending...'
                                                : isResetEmailSent
                                                  ? `Resend ${countdown > 0 ? `(${countdown}s)` : ''}`
                                                  : 'Send Link'}
                                        </button>
                                        <div className="align-self-center">
                                            |
                                        </div>
                                        {getHelpLink()}
                                    </div>
                                    <button
                                        type="button"
                                        className="ing-button-primary-left mx-auto mb-3 text-nowrap w-50 mt-5"
                                        onClick={handleBackToLogin}
                                    >
                                        <i className="bi bi-chevron-left"></i>
                                        {getText('msg_b5cd32158b70')}
                                    </button>
                                </>
                            )}
                        </div>
                        {!isResetEmailSent && (
                            <>
                                <div className="form-group d-grid gap-2 col-10 mx-auto text-center pt-3">
                                    {getHelpLink()}
                                </div>
                                <div className="form-group d-grid gap-2 col-10 mx-auto text-center pt-3">
                                    <small>
                                        <span className="text-danger">*</span>
                                        {getText('msg_7c118f042eaa')}
                                    </small>
                                </div>
                            </>
                        )}
                    </div>
                </form>
            )}
        </>
    );
}

export default RequestCredentialsForm;
